import React, { createContext, useState, useContext, useEffect, ReactNode, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

type Theme = 'light' | 'dark';

interface ThemeContextType {
    theme: Theme;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// RapID design tokens — anchored to the brand's signal green and deep patrol ink.
const tokens = {
    ink: '#0C1620',        // deepest surface, navbar in both modes
    gunmetal: '#15222F',   // dark-mode surfaces
    gunmetalHi: '#1C2C3C', // dark-mode raised surfaces
    mist: '#EEF1F4',       // light-mode app background
    signal: '#00DF36',     // brand green (logo)
    greenLight: '#0A9B3D', // accessible action green on light surfaces
    greenDark: '#2BE566',  // action green on dark surfaces
    alert: '#E5484D',      // hits / destructive
    amber: '#EFA13B',      // pending
};

const fontBody = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif";
const fontDisplay = "'Barlow Condensed', 'Arial Narrow', sans-serif";

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [theme, setTheme] = useState<Theme>(() => {
        return (localStorage.getItem('theme') as Theme) || 'dark'; // Default to dark
    });

    useEffect(() => {
        localStorage.setItem('theme', theme);
        document.body.className = '';
        document.body.classList.add(`theme-${theme}`);
    }, [theme]);

    const toggleTheme = () => {
        setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
    };

    const muiTheme = useMemo(() => {
        const dark = theme === 'dark';
        const divider = dark ? 'rgba(154, 172, 190, 0.16)' : 'rgba(28, 42, 56, 0.14)';

        return createTheme({
            palette: {
                mode: theme,
                primary: {
                    main: dark ? tokens.greenDark : tokens.greenLight,
                    contrastText: dark ? tokens.ink : '#ffffff',
                },
                secondary: {
                    main: dark ? '#9AACBE' : '#4A6076',
                },
                error: { main: tokens.alert },
                warning: { main: tokens.amber },
                success: { main: dark ? tokens.greenDark : tokens.greenLight },
                background: dark
                    ? { default: tokens.ink, paper: tokens.gunmetal }
                    : { default: tokens.mist, paper: '#ffffff' },
                text: dark
                    ? { primary: '#E9EEF3', secondary: '#9AACBE' }
                    : { primary: '#17222D', secondary: '#5A6B7C' },
                divider,
            },
            shape: { borderRadius: 10 },
            typography: {
                fontFamily: fontBody,
                h4: { fontFamily: fontDisplay, fontWeight: 600, letterSpacing: '0.01em' },
                h5: { fontFamily: fontDisplay, fontWeight: 600, letterSpacing: '0.01em' },
                h6: { fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.45rem', letterSpacing: '0.02em' },
                button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.01em' },
            },
            components: {
                MuiPaper: {
                    styleOverrides: {
                        root: {
                            backgroundImage: 'none',
                            border: `1px solid ${divider}`,
                            boxShadow: dark
                                ? '0 1px 2px rgba(0, 0, 0, 0.5)'
                                : '0 1px 3px rgba(16, 30, 44, 0.07)',
                        },
                    },
                },
                MuiCard: {
                    styleOverrides: {
                        root: {
                            border: `1px solid ${divider}`,
                        },
                    },
                },
                MuiButton: {
                    styleOverrides: {
                        root: { borderRadius: 8 },
                        containedPrimary: {
                            '&:hover': {
                                backgroundColor: dark ? tokens.signal : '#0B8536',
                            },
                        },
                    },
                },
                MuiTableHead: {
                    styleOverrides: {
                        root: {
                            '& .MuiTableCell-head': {
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                color: dark ? '#9AACBE' : '#5A6B7C',
                                backgroundColor: dark ? tokens.gunmetalHi : '#F5F7F9',
                                whiteSpace: 'nowrap',
                            },
                        },
                    },
                },
                MuiTableCell: {
                    styleOverrides: {
                        root: { borderBottomColor: divider },
                    },
                },
                MuiTableRow: {
                    styleOverrides: {
                        root: {
                            '&:hover': {
                                backgroundColor: dark ? 'rgba(43, 229, 102, 0.045)' : 'rgba(10, 155, 61, 0.045)',
                            },
                            '&:last-child .MuiTableCell-body': { borderBottom: 0 },
                        },
                    },
                },
                MuiDialog: {
                    styleOverrides: {
                        paper: {
                            border: `1px solid ${divider}`,
                            backgroundImage: 'none',
                        },
                    },
                },
                MuiDialogTitle: {
                    styleOverrides: {
                        root: { fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.4rem' },
                    },
                },
                MuiChip: {
                    styleOverrides: {
                        root: { fontWeight: 600 },
                    },
                },
                MuiSwitch: {
                    styleOverrides: {
                        root: {
                            '& .MuiSwitch-switchBase.Mui-checked': {
                                color: dark ? tokens.greenDark : tokens.greenLight,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: dark ? tokens.greenDark : tokens.greenLight,
                            },
                        },
                    },
                },
                MuiTablePagination: {
                    styleOverrides: {
                        root: { borderTop: `1px solid ${divider}` },
                    },
                },
            },
        });
    }, [theme]);

    const value = useMemo(() => ({ theme, toggleTheme }), [theme]);

    return (
        <ThemeContext.Provider value={value}>
            <MuiThemeProvider theme={muiTheme}>
                <CssBaseline />
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
